import type { CalBookingReference } from "@/lib/cal/booking-references";
import { isCalCredentialDefinitivelyUnusableError } from "@/lib/errors/cal";
import type { CalCredentials } from "@/models/provider/CalCredentials";
import {
  CAL_API_VERSION_BOOKING_CREATE_OVERRIDES,
  CAL_API_VERSION_BOOKING_RESCHEDULE,
  CAL_API_VERSION_BOOKING_RESCHEDULE_LEGACY,
  CAL_API_VERSION_BOOKINGS,
  CAL_API_VERSION_SLOTS,
} from "./constants";
import { isUnsupportedCalRescheduleRoute } from "./reschedule-compat";
import { refreshCredentialsIfExpired } from "./tokens";
import { type CalRequestAlertContext, fetchFromCalWithRetry } from "./utils";

export const CAL_SYSTEM = "cal";

export const CAL_IDENTIFIER_SYSTEM_USERS = `${CAL_SYSTEM}.users`;
export const CAL_IDENTIFIER_SYSTEM_USERS_USERNAME = `${CAL_SYSTEM}.users.username`;
export const CAL_IDENTIFIER_SYSTEM_USERS_EMAIL = `${CAL_SYSTEM}.users.email`;
export const CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS = `${CAL_SYSTEM}.access_tokens`;
export const CAL_IDENTIFIER_SYSTEM_PREVIOUS_ACCESS_TOKENS = `${CAL_SYSTEM}.previous_access_tokens`;
export const CAL_IDENTIFIER_SYSTEM_EVENT_TYPES = `${CAL_SYSTEM}.event_types`;
export const CAL_IDENTIFIER_SYSTEM_TEAM_EVENT_TYPES = `${CAL_SYSTEM}.team_event_types`;
export const CAL_IDENTIFIER_SYSTEM_BOOKINGS = `${CAL_SYSTEM}.bookings`;
export const CAL_IDENTIFIER_SYSTEM_TEAMS = `${CAL_SYSTEM}.teams`;
export const CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS = `${CAL_SYSTEM}.team_memberships`;

export const CAL_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT = `${CAL_SYSTEM}.access_token_expires_at`;
export const CAL_EXTENSION_URL_REFRESH_TOKEN = `${CAL_SYSTEM}.refresh_tokens`;
export const CAL_EXTENSION_URL_PREVIOUS_ACCESS_TOKEN_SET_AT = `${CAL_SYSTEM}.previous_access_token_set_at`;
export const CAL_EXTENSION_URL_VIDEO_CALL_URL = `${CAL_SYSTEM}.bookings.videoCallUrl`;
export const CAL_EXTENSION_URL_EVENT_TYPE_TITLE = `${CAL_SYSTEM}.event_types.title`;
export const CAL_EXTENSION_URL_EVENT_TYPE_SLUG = `${CAL_SYSTEM}.event_types.slug`;

export function getCalApiErrorStatusCode({ error }: { error: unknown }): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const statusCode = Reflect.get(error, "statusCode");
  return typeof statusCode === "number" ? statusCode : undefined;
}

function getCalApiErrorMessage({ error }: { error: unknown }): string | undefined {
  if (error instanceof Error && typeof error.message === "string") {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const message = Reflect.get(error, "message");
  return typeof message === "string" ? message : undefined;
}

/**
 * Cal can reject managed-user tokens before the stored expiry, so callers use this
 * predicate to decide when a forced refresh is worth one retry.
 */
export function isInvalidCalAccessTokenError(error: unknown): boolean {
  if (getCalApiErrorStatusCode({ error }) !== 401) {
    return false;
  }

  const normalizedMessage = getCalApiErrorMessage({ error })?.toLowerCase();
  if (!normalizedMessage) {
    return false;
  }

  return (
    normalizedMessage.includes("invalid access token") ||
    normalizedMessage.includes("oauth client not found")
  );
}

/**
 * Represents a busy time slot from a connected calendar.
 * The API may return times in either start/end or startTime/endTime format.
 */
export interface CalBusyTime {
  start: string;
  end: string;
  source?: string;
}

/**
 * Raw busy time response that may have different field names
 */
interface RawBusyTime {
  start?: string;
  end?: string;
  startTime?: string;
  endTime?: string;
  source?: string | null;
}

/**
 * Request parameters for fetching busy times
 */
export interface GetBusyTimesRequest {
  dateFrom: string;
  dateTo: string;
  loggedInUsersTz: string;
  calendarsToLoad: Array<{
    credentialId: number;
    externalId: string;
  }>;
}

/**
 * Response from the busy times API.
 * The data field may contain times in either start/end or startTime/endTime format.
 */
interface GetBusyTimesResponse {
  status: "success" | "error";
  data: RawBusyTime[];
}

export interface CalAvailableSlot {
  start: string;
  end?: string;
  attendeesCount?: number;
  bookingUid?: string;
}

export interface GetAvailableSlotsRequest {
  start: string;
  end: string;
  eventTypeId?: number;
  eventTypeSlug?: string;
  username?: string;
  timeZone?: string;
  duration?: number;
  format?: "range" | "time";
  bookingUidToReschedule?: string;
}

interface GetAvailableSlotsResponse {
  status: "success" | "error";
  data: Record<string, CalAvailableSlot[]>;
}

/**
 * Calendar credential info from Cal.com
 */
export interface CalCalendarCredential {
  id: number;
  type: string;
  credentialId: number;
  externalId: string;
  primary: boolean | null;
  readOnly: boolean;
  email: string | null;
  name: string;
  integrationTitle: string;
  isSelected?: boolean;
}

/**
 * Response from the calendars API
 */
interface GetCalendarsResponse {
  status: "success" | "error";
  data: {
    connectedCalendars: CalConnectedCalendar[];
    destinationCalendar: CalCalendarCredential | null;
  };
}

/**
 * Cal's URL segment for each integration, keyed by `integration.type`.
 *
 * Cal names the integration one way in a `/calendars` payload (`office365_calendar`) and another
 * in a path (`/calendars/office365/...`). Every per-integration route goes through this map, so
 * the two spellings are reconciled once.
 */
const CALENDAR_INTEGRATION_SLUGS: Record<string, string> = {
  google_calendar: "google",
  office365_calendar: "office365",
  apple_calendar: "apple",
};

/** The integration types Cal exposes per-integration routes for. */
export const CALENDAR_INTEGRATION_TYPES = Object.keys(CALENDAR_INTEGRATION_SLUGS);

/**
 * One external-calendar connection (Outlook, Google, Apple) for a Cal managed user.
 *
 * `error` is the field that separates "this provider selected no calendars" from "we cannot read
 * this provider's calendar at all". Cal answers `/calendars` with `status: "success"` even when a
 * connection's OAuth grant is dead: the entry carries `error` and omits `calendars`. Both Cal's
 * slot computation and ours then see zero busy time, so the provider's blocked time becomes
 * bookable with nothing thrown. Keep `error` modeled and read it — see
 * `classifyExternalCalendarState`.
 */
export interface CalConnectedCalendar {
  integration?: {
    type?: string;
    title?: string;
  } | null;
  credentialId: number;
  calendars?: CalCalendarCredential[] | null;
  error?: { message?: string } | null;
}

export interface DisconnectCalendarRequest {
  /** Cal's `integration.type`, e.g. `"google_calendar"`. */
  integrationType: string;
  /** Cal's id for the connection, from `connectedCalendars[].credentialId`. */
  credentialId: number;
}

interface CreateCalClientOptions {
  alertContext?: CalRequestAlertContext;
  forceRefresh?: boolean;
  shouldCaptureInvalidAccessToken?: boolean;
  shouldRetryInvalidAccessToken?: boolean;
}

interface RefreshCalClientAccessTokenOptions {
  currentAccessToken?: string;
  forceRefresh?: boolean;
}

interface CalClientOptions {
  alertContext?: CalRequestAlertContext;
  refreshAccessToken?: (options: RefreshCalClientAccessTokenOptions) => Promise<string>;
  shouldCaptureInvalidAccessToken?: boolean;
}

interface HasConnectedCalendarOptions {
  shouldThrowOnInvalidAccessToken?: boolean;
}

interface RunWithCalClientInvalidTokenRetryInput<T> {
  calCredentials: CalCredentials;
  alertContext?: CalRequestAlertContext;
  onInvalidAccessTokenRetry?: () => void;
  operation: (calClient: CalClient) => Promise<T>;
}

interface CalClientRequestInput {
  accessToken: string;
  shouldCaptureInvalidAccessToken: boolean;
}

type CalDayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

export interface CalScheduleAvailability {
  days: CalDayOfWeek[];
  startTime: string;
  endTime: string;
}

export interface CalScheduleOverride {
  date: string;
  startTime: string;
  endTime: string;
}

export interface CreateScheduleRequest {
  name: string;
  timeZone: string;
  availability?: CalScheduleAvailability[];
  isDefault: boolean;
  overrides?: CalScheduleOverride[];
}

export type UpdateScheduleRequest = Partial<CreateScheduleRequest>;

export interface CalSchedule {
  id: number;
  ownerId: number;
  name: string;
  timeZone: string;
  availability: CalScheduleAvailability[];
  isDefault: boolean;
  overrides: CalScheduleOverride[];
}

interface CreateScheduleResponse {
  status: "success" | "error";
  data: CalSchedule;
}

interface UpdateScheduleResponse {
  status: "success" | "error";
  data: CalSchedule;
}

interface GetScheduleResponse {
  status: "success" | "error";
  data: CalSchedule;
}

export interface CalDisabledValue {
  disabled: true;
}

export interface CalBookingLimitValues {
  day?: number;
  week?: number;
  month?: number;
  year?: number;
}

export interface CalBookingWindowDays {
  type: "businessDays" | "calendarDays" | "rolling";
  value: number;
  rolling?: boolean;
}

export interface CalBookingWindowRange {
  type: "range";
  value: string[];
}

export interface CalUnlimitedBookingWindow {
  type: "unlimited";
  value?: number;
  rolling?: boolean;
}

export type CalBookingWindow =
  | CalBookingWindowDays
  | CalBookingWindowRange
  | CalUnlimitedBookingWindow;
export type CalBookingWindowResponse = CalBookingWindow | CalDisabledValue | CalBookingWindow[];

export interface CreateEventTypeRequest {
  lengthInMinutes: number;
  title: string;
  slug: string;
  description?: string;
  locations?: Array<{
    type: string;
    [key: string]: unknown;
  }>;
  disableGuests?: boolean;
  minimumBookingNotice?: number;
  beforeEventBuffer?: number;
  afterEventBuffer?: number;
  scheduleId?: number | null;
  slotInterval?: number;
  // Add other optional fields as needed
}

interface CreateEventTypeResponse {
  status: "success" | "error";
  data: CalEventType;
}

interface GetEventTypeResponse {
  status: "success" | "error";
  data: CalEventType;
}

export interface CalEventTypeHost {
  id?: number | null;
  userId?: number | null;
  username?: string | null;
  name?: string | null;
  user?: {
    id?: number | null;
    username?: string | null;
    name?: string | null;
  } | null;
}

export interface CalEventType {
  id: number;
  lengthInMinutes: number;
  title: string;
  slug: string;
  description?: string;
  locations?: Array<{
    type: string;
    [key: string]: unknown;
  }>;
  bookingFields?: Array<{
    type: string;
    required: boolean;
    label: string;
    [key: string]: unknown;
  }>;
  disableGuests?: boolean;
  slotInterval?: number;
  minimumBookingNotice?: number;
  beforeEventBuffer?: number;
  afterEventBuffer?: number;
  scheduleId?: number | null;
  bookingLimitsCount?: CalBookingLimitValues | CalDisabledValue;
  onlyShowFirstAvailableSlot?: boolean;
  bookingLimitsDuration?: CalBookingLimitValues | CalDisabledValue;
  bookingWindow?: CalBookingWindowResponse;
  offsetStart?: number;
  bookerLayouts?: {
    defaultLayout: "month" | "week" | "column";
    enabledLayouts: Array<"month" | "week" | "column">;
  };
  confirmationPolicy?: {
    type: string;
    noticeThreshold?: {
      unit: string;
      count: number;
    };
  };
  recurrence?: {
    interval: number;
    occurrences: number;
    frequency: "YEARLY" | "MONTHLY" | "WEEKLY" | "DAILY";
  };
  requiresBookerEmailVerification?: boolean;
  hideCalendarNotes?: boolean;
  lockTimeZoneToggleOnBookingPage?: boolean;
  color?: {
    lightThemeHex: string;
    darkThemeHex: string;
  };
  seats?: {
    seatsPerTimeSlot: number;
    showAttendeeInfo: boolean;
    showAvailabilityCount: boolean;
  };
  customName?: string;
  destinationCalendar?: {
    integration: string;
    externalId: string;
  };
  useDestinationCalendarEmail?: boolean;
  hideCalendarEventDetails?: boolean;
  // MANAGED scheduling fields
  ownerId?: number | null;
  userId?: number | null;
  parentEventTypeId?: number | null;
  schedulingType?:
    | "COLLECTIVE"
    | "ROUND_ROBIN"
    | "MANAGED"
    | "collective"
    | "roundRobin"
    | "managed"
    | null;
  teamId?: number | null;
  users?: Array<{
    id?: number | null;
    username?: string | null;
    name?: string | null;
  }>;
  hosts?: Array<CalEventTypeHost | string>;
  hostsCount?: number;
  assignAllTeamMembers?: boolean;
}

export interface CreateTeamRequest {
  name: string;
  slug?: string;
  logoUrl?: string;
  calVideoLogo?: string;
  appLogo?: string;
  appIconLogo?: string;
  bio?: string;
  hideBranding?: boolean;
  isPrivate?: boolean;
  hideBookATeamMember?: boolean;
  metadata?: Record<string, unknown>;
  theme?: string;
  brandColor?: string;
  darkBrandColor?: string;
  bannerUrl?: string;
  timeFormat?: number;
  timeZone?: string;
  weekStart?: string;
  autoAcceptCreator?: boolean;
}

export interface CalTeam {
  id: number;
  parentId?: number;
  name: string;
  slug: string;
  logoUrl?: string;
  calVideoLogo?: string;
  appLogo?: string;
  appIconLogo?: string;
  bio?: string;
  hideBranding?: boolean;
  isOrganization?: boolean;
  isPrivate?: boolean;
  hideBookATeamMember?: boolean;
  metadata?: Record<string, unknown>;
  theme?: string;
  brandColor?: string;
  darkBrandColor?: string;
  bannerUrl?: string;
  timeFormat?: number;
  timeZone?: string;
  weekStart?: string;
}

interface CreateTeamResponse {
  status: "success" | "error";
  data: CalTeam;
}

export interface CreateTeamEventTypeRequest {
  lengthInMinutes: number;
  title: string;
  slug: string;
  description?: string;
  locations?: Array<{
    type: string;
    [key: string]: unknown;
  }>;
  disableGuests?: boolean;
  minimumBookingNotice?: number;
  beforeEventBuffer?: number;
  afterEventBuffer?: number;
  scheduleId?: number | null;
  slotInterval?: number;
  bookingLimitsCount?: CalBookingLimitValues | CalDisabledValue;
  onlyShowFirstAvailableSlot?: boolean;
  bookingLimitsDuration?: CalBookingLimitValues | CalDisabledValue;
  bookingWindow?: CalBookingWindow | CalDisabledValue;
  schedulingType?: "COLLECTIVE" | "ROUND_ROBIN" | "MANAGED";
  hosts?: Array<{
    userId: number;
    isFixed?: boolean;
    mandatory?: boolean;
    priority?: "lowest" | "low" | "medium" | "high" | "highest";
  }>;
  assignAllTeamMembers?: boolean;
  // Host confirmation gate (see UpdateTeamEventTypeRequest). Set at create so a round-robin
  // event mirrors its source event's paid-booking hold from the first booking.
  confirmationPolicy?: CalConfirmationPolicyInput;
}

interface CreateTeamEventTypeResponse {
  status: "success" | "error";
  data: CalEventType | CalEventType[]; // MANAGED returns array, others return single
}

export interface GetTeamEventTypesResponse {
  status: "success" | "error";
  data: CalEventType[];
}

export interface GetEventTypesResponse {
  status: "success" | "error";
  data: CalEventType[];
}

interface CalBookingActionError {
  code?: string;
  message?: string;
}

export interface CalBookingActionResponse {
  data?: CalBookingResponseData | CalBookingResponseData[];
  status: "success" | "error";
  error?: CalBookingActionError;
}

export interface CalCancelBookingRequest {
  bookingId: string;
  cancellationReason?: string;
  cancelSubsequentBookings?: boolean;
  seatUid?: string;
}

/**
 * The provider-confirmed "Book anyway" bypass flags, shared by the create and reschedule request
 * shapes. Both `usesCalOverrideFlags` (api-version selection) and callers shaping payloads key off
 * this one interface, so a future flag has a single declaration to extend.
 */
export interface CalOverrideFlags {
  /**
   * When true and the authenticated Cal user is a host of the event type, Cal bypasses
   * availability conflict checks — i.e. permits intentional double-booking/overlap.
   */
  allowConflicts?: boolean;
  /**
   * When true and the authenticated Cal user is a host of the event type, Cal bypasses
   * booking-window checks and permits bookings outside configured availability. Only supported
   * on 2026-02-25. Cal's out-of-bounds check bundles both the future-window limit AND the
   * minimum-booking-notice floor, so this flag also waives "too soon" rejects.
   */
  allowBookingOutOfBounds?: boolean;
  /**
   * When true and the authenticated Cal user is a host of the event type, Cal bypasses the
   * event-type / team / per-host booking-count and duration limits (e.g. the per-day cap). Only
   * supported on 2026-02-25.
   */
  skipBookingLimits?: boolean;
}

export interface CalCreateBookingRequest extends CalOverrideFlags {
  start: string;
  attendee: {
    name: string;
    email: string;
    timeZone: string;
    language?: string;
    phoneNumber?: string;
  };
  eventTypeId?: number;
  eventTypeSlug?: string;
  username?: string;
  metadata?: Record<string, unknown>;
  // A plain string is coerced into a meeting URL (2024-08-13 only). The 2026-02-25 override schema
  // validates the location type against the event type's configured locations (integration-only
  // for us), so no custom-URL location can ride an override create — omit it there. Phone-call
  // event types need the structured attendee-phone location on both schemas; the attendee's number
  // also goes in attendee.phoneNumber.
  location?: string | { type: "attendeePhone"; phone: string };
  // Deprecated Cal field, documented on the 2026-02-25 create input: "Meeting URL just for this
  // booking". Override creates use it to carry the Daily URL, because `location` can't (ENG-6074).
  meetingUrl?: string;
  guests?: string[];
  bookingFieldsResponses?: Record<string, unknown>;
  lengthInMinutes?: number;
  rescheduleUid?: string;
}

export interface CalBookingHost {
  id?: number;
  name?: string;
  email?: string;
  username?: string;
  timeZone?: string;
}

export interface CalBookingAttendee {
  name?: string;
  email?: string;
  timeZone?: string;
  language?: string;
  absent?: boolean;
}

export interface CalBookingEventTypeSummary {
  id?: number;
  slug?: string;
  title?: string;
}

/**
 * Cal's reschedule endpoint can return single, recurring, and seated booking shapes.
 * We model the common fields plus recursive `bookings` for recurring variants.
 */
export interface CalBookingResponseData extends Record<string, unknown> {
  absentHost?: boolean;
  attendees?: CalBookingAttendee[];
  bookingFieldsResponses?: Record<string, unknown>;
  bookings?: CalBookingResponseData[];
  cancellationReason?: string;
  cancelledByEmail?: string;
  createdAt?: string;
  description?: string;
  duration?: number;
  end?: string;
  eventType?: CalBookingEventTypeSummary;
  eventTypeId?: number;
  guests?: string[];
  hosts?: CalBookingHost[];
  icsUid?: string;
  id?: number;
  location?: string;
  meetingUrl?: string;
  metadata?: Record<string, unknown>;
  rating?: number;
  /** Not returned by Cal v2 `2024-08-13`, despite being modelled here — see `booking-references.ts`. */
  references?: CalBookingReference[];
  rescheduledByEmail?: string;
  rescheduledFromUid?: string;
  rescheduledToUid?: string;
  reschedulingReason?: string;
  seatUid?: string;
  start?: string;
  status?: "accepted" | "cancelled" | "pending" | "rejected" | string;
  title?: string;
  uid?: string;
  updatedAt?: string;
}

export interface CalCreateBookingResponse {
  status: "success" | "error";
  data: CalBookingResponseData | CalBookingResponseData[];
  error?: CalBookingActionError;
}

interface CalRescheduleBookingRequestBase extends CalOverrideFlags {
  bookingUid: string;
  emailVerificationCode?: string;
  rescheduledBy?: string;
  start: string;
}

export interface CalStandardRescheduleBookingRequest extends CalRescheduleBookingRequestBase {
  reschedulingReason?: string;
  seatUid?: never;
}

export interface CalSeatedRescheduleBookingRequest extends CalRescheduleBookingRequestBase {
  reschedulingReason?: never;
  seatUid: string;
}

export type CalRescheduleBookingRequest =
  | CalSeatedRescheduleBookingRequest
  | CalStandardRescheduleBookingRequest;

/**
 * Whether a create/reschedule goes out as a provider-confirmed override ("Book anyway").
 * Overrides ride Cal's 2026-02-25 schema — the only one honoring the bypass flags — which
 * changes both the payload shape a caller may send (no plain-string `location`, see ENG-5128)
 * and the api version this client selects. Single predicate over the shared flags interface so
 * those two decisions can never disagree again.
 */
export function usesCalOverrideFlags(request: CalOverrideFlags): boolean {
  return Boolean(
    request.allowConflicts || request.allowBookingOutOfBounds || request.skipBookingLimits,
  );
}

/**
 * Cal documents reschedule responses as a status envelope with booking data.
 * Transport-level failures are thrown by `fetchFromCalWithRetry` before this returns, but Cal can
 * also hand back a 2xx envelope with `status: "error"` (e.g. availability rejects) — `error` carries
 * the reason in that case, mirroring `CalCreateBookingResponse`.
 */
export interface CalRescheduleBookingResponse {
  status: "success" | "error";
  data: CalBookingResponseData;
  error?: CalBookingActionError;
}

export interface UpdateTeamEventTypeRequest {
  title?: string;
  slug?: string;
  description?: string;
  lengthInMinutes?: number;
  locations?: Array<{
    type: string;
    [key: string]: unknown;
  }>;
  disableGuests?: boolean;
  minimumBookingNotice?: number;
  beforeEventBuffer?: number;
  afterEventBuffer?: number;
  scheduleId?: number | null;
  slotInterval?: number;
  bookingLimitsCount?: CalBookingLimitValues | CalDisabledValue;
  onlyShowFirstAvailableSlot?: boolean;
  bookingLimitsDuration?: CalBookingLimitValues | CalDisabledValue;
  bookingWindow?: CalBookingWindow | CalDisabledValue;
  assignAllTeamMembers?: boolean;
  hosts?: Array<{
    userId: number;
    isFixed?: boolean;
    mandatory?: boolean;
    priority?: "lowest" | "low" | "medium" | "high" | "highest";
  }>;
  // Host confirmation gate. `{ type: "always" }` makes every booking land pending until the
  // host confirms; `{ disabled: true }` turns it off. Used to hold paid bookings until payment.
  confirmationPolicy?: CalConfirmationPolicyInput;
}

/** Cal.com confirmation-policy input for event-type updates. */
export type CalConfirmationPolicyInput =
  | {
      type: "always" | "time";
      noticeThreshold?: { unit: string; count: number };
      blockUnconfirmedBookingsInBooker?: boolean;
    }
  | { disabled: true };

/**
 * The host-confirmation gate that holds a booking pending until payment. Paid appointment types
 * (a linked Stripe price) require confirmation so the booking lands pending until the payment
 * webhook confirms it; unpaid types disable the gate so bookings confirm instantly. Keep every
 * event type that can be booked for a paid appointment type (individual + round-robin) in sync
 * via this single source of truth.
 */
export function paymentHoldConfirmationPolicy(
  requiresPayment: boolean,
): CalConfirmationPolicyInput {
  return requiresPayment
    ? { type: "always", blockUnconfirmedBookingsInBooker: true }
    : { disabled: true };
}

interface UpdateTeamEventTypeResponse {
  status: "success" | "error";
  data: CalEventType;
}

export interface UpdateEventTypeRequest {
  title?: string;
  slug?: string;
  description?: string;
  lengthInMinutes?: number;
  locations?: Array<{
    type: string;
    [key: string]: unknown;
  }>;
  disableGuests?: boolean;
  minimumBookingNotice?: number;
  beforeEventBuffer?: number;
  afterEventBuffer?: number;
  scheduleId?: number | null;
  slotInterval?: number;
}

interface UpdateEventTypeResponse {
  status: "success" | "error";
  data: CalEventType;
}

export interface CreateTeamMembershipRequest {
  userId: number;
  accepted: boolean;
  role: "MEMBER" | "ADMIN" | "OWNER";
}

export interface CalTeamMembership {
  id: number;
  teamId: number;
  userId: number;
  accepted: boolean;
  role: "MEMBER" | "ADMIN" | "OWNER";
  disableImpersonation: boolean;
}

interface CreateTeamMembershipResponse {
  status: "success" | "error";
  data: CalTeamMembership;
}

interface ListTeamMembershipsResponse {
  status: "success" | "error";
  data: CalTeamMembership[];
}

interface GetTeamResponse {
  status: "success" | "error";
  data: CalTeam;
}

interface GetTeamsResponse {
  status: "success" | "error";
  data: Array<{ id: number; name: string; slug: string }>;
}

interface CalPlatformRequestContext {
  orgId: string;
  headers: {
    "x-cal-client-id": string;
    "x-cal-secret-key": string;
  };
}

/**
 * CalTeamClient handles team-level operations that use
 * platform credentials (client ID and secret) rather than user access tokens.
 */
export class CalTeamClient {
  private getPlatformRequestContext(): CalPlatformRequestContext {
    const orgId = process.env.NEXT_PUBLIC_CAL_ORG_ID;
    const clientId = process.env.NEXT_PUBLIC_CAL_CLIENT_ID;
    const clientSecret = process.env.CAL_CLIENT_SECRET;

    if (!orgId) {
      throw new Error("NEXT_PUBLIC_CAL_ORG_ID environment variable is not set");
    }

    if (!clientId || !clientSecret) {
      throw new Error(
        "NEXT_PUBLIC_CAL_CLIENT_ID and CAL_CLIENT_SECRET environment variables are required",
      );
    }

    return {
      orgId,
      headers: {
        "x-cal-client-id": clientId,
        "x-cal-secret-key": clientSecret,
      },
    };
  }

  /**
   * Gets all teams in the organization
   * Note: Requires organization admin or owner permissions and platform credentials
   */
  async getTeams(): Promise<GetTeamsResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<GetTeamsResponse>(`/organizations/${orgId}/teams`, {
      method: "GET",
      headers,
    });
  }

  /**
   * Gets a specific team by ID from the organization
   * Note: Requires organization admin or owner permissions and platform credentials
   * @see https://cal.com/docs/api-reference/v2/orgs-teams/get-a-team
   *
   * @param teamId - The Cal.com team ID
   * @returns The team details
   */
  async getTeam({ teamId }: { teamId: number }): Promise<GetTeamResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<GetTeamResponse>(`/organizations/${orgId}/teams/${teamId}`, {
      method: "GET",
      headers,
    });
  }

  /**
   * Updates a team in the organization. Used to rename a practice's team slug (ENG-6123).
   * @see https://cal.com/docs/api-reference/v2/orgs-teams/update-a-team
   */
  async updateTeam({
    teamId,
    update,
  }: {
    teamId: number;
    update: Partial<Pick<CalTeam, "name" | "slug">>;
  }): Promise<GetTeamResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<GetTeamResponse>(`/organizations/${orgId}/teams/${teamId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(update),
    });
  }

  /**
   * Creates a new team in Cal.com using the organization-scoped API
   * Note: Requires organization admin or owner permissions and platform credentials
   */
  async createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<CreateTeamResponse>(`/organizations/${orgId}/teams`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    });
  }

  /**
   * Creates a new team event type in Cal.com for practice-wide appointment types
   * Team event types support collective or round-robin scheduling across multiple providers
   *
   * @param teamId - The Cal.com team ID
   * @param request - Team event type configuration including scheduling type and assigned hosts
   * @returns The created event type with team information
   */
  async createTeamEventType({
    teamId,
    request,
  }: {
    teamId: number;
    request: CreateTeamEventTypeRequest;
  }): Promise<CreateTeamEventTypeResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<CreateTeamEventTypeResponse>(
      `/organizations/${orgId}/teams/${teamId}/event-types`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Lists team event types for a Cal.com team.
   *
   * Managed team responses reliably include parent rows and may include host metadata, but
   * child managed event rows are not guaranteed. Use `getEventTypes()` with `username` +
   * `eventSlug` when child event IDs are required.
   *
   * @see https://cal.com/docs/api-reference/v2/teams-event-types/get-team-event-types
   */
  async getTeamEventTypes({
    teamId,
    hostsLimit,
  }: {
    teamId: number;
    hostsLimit?: number;
  }): Promise<GetTeamEventTypesResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    const query = new URLSearchParams();
    if (hostsLimit !== undefined) {
      query.set("hostsLimit", String(hostsLimit));
    }
    const queryString = query.toString();
    const endpoint = queryString
      ? `/organizations/${orgId}/teams/${teamId}/event-types?${queryString}`
      : `/organizations/${orgId}/teams/${teamId}/event-types`;

    return fetchFromCalWithRetry<GetTeamEventTypesResponse>(endpoint, {
      method: "GET",
      headers,
    });
  }

  /**
   * Lists user event types using platform credentials.
   *
   * Use `username` + `eventSlug` to resolve managed child event IDs. Live Cal responses can
   * accept `usernames` + `eventSlug` while returning a synthetic dynamic-group row instead of
   * canonical managed child event rows, so callers should not use bulk usernames queries for
   * managed child verification.
   *
   * @see https://cal.com/docs/api-reference/v2/event-types/get-all-event-types
   */
  async getEventTypes({
    username,
    usernames,
    eventSlug,
  }: {
    username?: string;
    usernames?: string[];
    eventSlug?: string;
  }): Promise<GetEventTypesResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();
    const normalizedUsername = username?.trim();
    const normalizedUsernames = Array.from(
      new Set(
        (usernames ?? [])
          .map((candidate) => candidate.trim())
          .filter((candidate) => candidate.length > 0),
      ),
    );
    const normalizedEventSlug = eventSlug?.trim();

    if (normalizedUsername && normalizedUsernames.length > 0) {
      throw new Error("Provide either username or usernames when listing Cal.com event types");
    }

    if (!normalizedUsername && normalizedUsernames.length === 0) {
      throw new Error("username or usernames is required when listing Cal.com event types");
    }

    if (normalizedEventSlug && normalizedUsernames.length > 0) {
      throw new Error(
        "eventSlug cannot be used with usernames; use username with eventSlug or omit eventSlug for multi-user requests",
      );
    }

    const query = new URLSearchParams({
      orgId,
    });

    if (normalizedUsername) {
      query.set("username", normalizedUsername);
    }

    if (normalizedUsernames.length > 0) {
      query.set("usernames", normalizedUsernames.join(","));
    }

    if (normalizedEventSlug) {
      query.set("eventSlug", normalizedEventSlug);
    }

    return fetchFromCalWithRetry<GetEventTypesResponse>(`/event-types?${query.toString()}`, {
      method: "GET",
      headers,
    });
  }

  /**
   * Adds a user to a Cal.com team by creating a team membership
   *
   * @param teamId - The Cal.com team ID
   * @param request - Membership configuration including user ID, accepted status, and role
   * @returns The created team membership
   */
  async createTeamMembership({
    teamId,
    request,
  }: {
    teamId: number;
    request: CreateTeamMembershipRequest;
  }): Promise<CreateTeamMembershipResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<CreateTeamMembershipResponse>(
      `/organizations/${orgId}/teams/${teamId}/memberships`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Fetches a single team event type by ID.
   * Uses org-level platform credentials — no managed user token required.
   */
  async getTeamEventType({
    teamId,
    eventTypeId,
  }: {
    teamId: number;
    eventTypeId: number;
  }): Promise<CalEventType> {
    const { orgId, headers } = this.getPlatformRequestContext();
    const response = await fetchFromCalWithRetry<{
      status: "success" | "error";
      data: CalEventType;
    }>(`/organizations/${orgId}/teams/${teamId}/event-types/${eventTypeId}`, {
      method: "GET",
      headers,
    });
    if (response.status !== "success") {
      throw new Error(
        `Failed to get team event type ${eventTypeId}: API returned status "${response.status}"`,
      );
    }
    return response.data;
  }

  /**
   * Deletes a team event type from Cal.com
   *
   * @param teamId - The Cal.com team ID
   * @param eventTypeId - The event type ID to delete
   * @returns Success status
   */
  async deleteTeamEventType({
    teamId,
    eventTypeId,
  }: {
    teamId: number;
    eventTypeId: number;
  }): Promise<{ status: "success" | "error"; data: Record<string, never> }> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<{ status: "success" | "error"; data: Record<string, never> }>(
      `/organizations/${orgId}/teams/${teamId}/event-types/${eventTypeId}`,
      {
        method: "DELETE",
        headers,
      },
    );
  }

  /**
   * Updates an existing team event type in Cal.com
   *
   * @param teamId - The Cal.com team ID
   * @param eventTypeId - The event type ID to update
   * @param request - The update payload (partial, only includes fields to change)
   * @returns Updated event type
   */
  async updateTeamEventType({
    teamId,
    eventTypeId,
    request,
  }: {
    teamId: number;
    eventTypeId: number;
    request: UpdateTeamEventTypeRequest;
  }): Promise<UpdateTeamEventTypeResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    const response = await fetchFromCalWithRetry<UpdateTeamEventTypeResponse>(
      `/organizations/${orgId}/teams/${teamId}/event-types/${eventTypeId}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(request),
      },
    );

    if (response.status !== "success") {
      throw new Error(
        `Failed to update team event type ${eventTypeId}: API returned status "${response.status}"`,
      );
    }

    return response;
  }

  /**
   * Lists memberships for a Cal.com team. Without `take`, Cal returns only its default first page.
   *
   * @param teamId - The Cal.com team ID
   * @param take - Page size
   * @param skip - Number of memberships to skip
   * @returns One page of team memberships
   */
  async listTeamMemberships({
    teamId,
    take,
    skip,
  }: {
    teamId: number;
    take?: number;
    skip?: number;
  }): Promise<ListTeamMembershipsResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();
    const query = new URLSearchParams();
    if (take != null) {
      query.set("take", String(take));
    }
    if (skip != null) {
      query.set("skip", String(skip));
    }
    const queryString = query.toString() ? `?${query.toString()}` : "";

    return fetchFromCalWithRetry<ListTeamMembershipsResponse>(
      `/organizations/${orgId}/teams/${teamId}/memberships${queryString}`,
      {
        method: "GET",
        headers,
      },
    );
  }

  /**
   * Updates an existing team membership in Cal.com (e.g., to change the role)
   *
   * @param teamId - The Cal.com team ID
   * @param membershipId - The membership ID to update
   * @param request - Updated membership configuration
   * @returns The updated team membership
   */
  async updateTeamMembership({
    teamId,
    membershipId,
    request,
  }: {
    teamId: number;
    membershipId: number;
    request: Partial<CreateTeamMembershipRequest>;
  }): Promise<CreateTeamMembershipResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<CreateTeamMembershipResponse>(
      `/organizations/${orgId}/teams/${teamId}/memberships/${membershipId}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Removes a user from a Cal.com team by deleting their team membership
   *
   * @param teamId - The Cal.com team ID
   * @param membershipId - The membership ID to delete
   * @returns Success status
   */
  async deleteTeamMembership({
    teamId,
    membershipId,
  }: {
    teamId: number;
    membershipId: number;
  }): Promise<{ status: "success" | "error" }> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<{ status: "success" | "error" }>(
      `/organizations/${orgId}/teams/${teamId}/memberships/${membershipId}`,
      {
        method: "DELETE",
        headers,
      },
    );
  }

  /**
   * Deletes a Cal.com team from the organization
   * WARNING: This will delete the team and all its event types
   * @see https://cal.com/docs/api-reference/v2/orgs-teams/delete-a-team
   *
   * @param teamId - The Cal.com team ID to delete
   * @returns The deleted team data
   */
  async deleteTeam({ teamId }: { teamId: number }): Promise<GetTeamResponse> {
    const { orgId, headers } = this.getPlatformRequestContext();

    return fetchFromCalWithRetry<GetTeamResponse>(`/organizations/${orgId}/teams/${teamId}`, {
      method: "DELETE",
      headers,
    });
  }
}

/**
 * CalClient handles user-level operations that require a user access token.
 */
export class CalClient {
  constructor(
    private accessToken: string,
    private readonly options: CalClientOptions = {},
  ) {}

  private async hasCalendarConnection(
    endpoint: string,
    options: HasConnectedCalendarOptions = {},
  ): Promise<boolean> {
    try {
      const response = await this.get<{ status: "success" | "error" }>(endpoint);
      return response.status === "success";
    } catch (error) {
      if (options.shouldThrowOnInvalidAccessToken && isInvalidCalAccessTokenError(error)) {
        throw error;
      }

      return false;
    }
  }

  /**
   * Creates a CalTeamClient for team management operations
   * that use platform credentials instead of user access tokens
   */
  static forTeam(): CalTeamClient {
    return new CalTeamClient();
  }

  async createEventType(request: CreateEventTypeRequest): Promise<CreateEventTypeResponse> {
    return this.post<CreateEventTypeResponse>("/event-types", request);
  }

  async getEventType(id: number): Promise<CalEventType> {
    const response = await this.get<GetEventTypeResponse>(`/event-types/${id}`);
    if (response.status !== "success") {
      throw new Error(`Failed to get event type ${id}: API returned status "${response.status}"`);
    }
    return response.data;
  }

  async updateEventType({
    eventTypeId,
    request,
  }: {
    eventTypeId: number;
    request: UpdateEventTypeRequest;
  }): Promise<CalEventType> {
    const response = await this.patch<UpdateEventTypeResponse>(
      `/event-types/${eventTypeId}`,
      request,
    );
    if (response.status !== "success") {
      throw new Error(`Failed to update event type ${eventTypeId}`);
    }
    return response.data;
  }

  async deleteEventType(id: number): Promise<{ status: "success" | "error" }> {
    return this.delete<{ status: "success" | "error" }>(`/event-types/${id}`);
  }

  async cancelBooking({
    bookingId,
    cancellationReason,
    cancelSubsequentBookings,
    seatUid,
  }: CalCancelBookingRequest): Promise<CalBookingActionResponse> {
    const body = {
      ...(cancellationReason ? { cancellationReason } : {}),
      ...(typeof cancelSubsequentBookings === "boolean" ? { cancelSubsequentBookings } : {}),
      ...(seatUid ? { seatUid } : {}),
    };

    return this.post<CalBookingActionResponse>(
      `/bookings/${bookingId}/cancel`,
      Object.keys(body).length > 0 ? body : undefined,
      {
        apiVersion: CAL_API_VERSION_BOOKINGS,
      },
    );
  }

  /**
   * Confirms a booking that is pending host confirmation (requiresConfirmation event
   * types). Used by the embed payment flow to release a held booking once payment
   * succeeds — Cal then emits BOOKING_CONFIRMED, which finalizes the appointment.
   */
  async confirmBooking(bookingUid: string): Promise<CalBookingActionResponse> {
    return this.post<CalBookingActionResponse>(`/bookings/${bookingUid}/confirm`, undefined, {
      apiVersion: CAL_API_VERSION_BOOKINGS,
    });
  }

  /**
   * Fetches a single booking by its Cal UID. Returns null when Cal has no such booking — the
   * reconciler treats that as a real, alertable "orphaned booking" signal (split-brain), not an
   * error. See ENG-4616.
   */
  async getBooking(bookingUid: string): Promise<CalBookingResponseData | null> {
    try {
      const response = await this.get<CalBookingActionResponse>(`/bookings/${bookingUid}`, {
        apiVersion: CAL_API_VERSION_BOOKINGS,
      });
      const data = Array.isArray(response.data) ? response.data[0] : response.data;
      return data ?? null;
    } catch (error) {
      if (getCalApiErrorStatusCode({ error }) === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Creates a Cal.com booking for the current managed user.
   */
  async createBooking(request: CalCreateBookingRequest): Promise<CalCreateBookingResponse> {
    return this.post<CalCreateBookingResponse>("/bookings", request, {
      apiVersion: usesCalOverrideFlags(request)
        ? CAL_API_VERSION_BOOKING_CREATE_OVERRIDES
        : CAL_API_VERSION_BOOKINGS,
    });
  }

  /**
   * Reschedules an existing booking to a new start time.
   */
  async rescheduleBooking({
    bookingUid,
    ...request
  }: CalRescheduleBookingRequest): Promise<CalRescheduleBookingResponse> {
    const endpoint = `/bookings/${bookingUid}/reschedule`;
    const usesOverrideFlags = usesCalOverrideFlags(request);

    try {
      return await this.post<CalRescheduleBookingResponse>(endpoint, request, {
        apiVersion: CAL_API_VERSION_BOOKING_RESCHEDULE,
      });
    } catch (error) {
      const statusCode = getCalApiErrorStatusCode({ error });
      const message = error instanceof Error ? error.message : undefined;
      // The override bypasses are 2026-02-25-only, so never silently downgrade an override reschedule
      // to the legacy schema — that would drop the flags and re-hit the conflict we're overriding.
      if (usesOverrideFlags || !isUnsupportedCalRescheduleRoute({ statusCode, message })) {
        throw error;
      }

      return this.post<CalRescheduleBookingResponse>(endpoint, request, {
        apiVersion: CAL_API_VERSION_BOOKING_RESCHEDULE_LEGACY,
      });
    }
  }

  /**
   * @returns true if at least one calendar connection is successful
   */
  async hasConnectedCalendar(options: HasConnectedCalendarOptions = {}): Promise<boolean> {
    const endpoints = Object.values(CALENDAR_INTEGRATION_SLUGS).map(
      (slug) => `/calendars/${slug}/check`,
    );

    for (const endpoint of endpoints) {
      if (await this.hasCalendarConnection(endpoint, options)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Reports whether the stored credential for one integration still works.
   *
   * `GET /calendars` is not enough on its own. A reconnect can repopulate the calendar list and
   * the destination calendar while leaving a credential Cal still refuses to query with — the
   * connection then carries no `error`, lists calendars, and returns zero busy times, which reads
   * as "this clinician has an empty week" (ENG-5401). This endpoint is the one that answers
   * honestly in that state.
   *
   * Three-valued on purpose: `undefined` means "could not determine" — an unrecognised
   * integration slug, or a transient failure — and must never be treated as broken.
   */
  async isCalendarCredentialUsable(integrationType: string): Promise<boolean | undefined> {
    const slug = CALENDAR_INTEGRATION_SLUGS[integrationType];
    if (!slug) {
      return undefined;
    }

    try {
      const response = await this.get<{ status: "success" | "error" }>(`/calendars/${slug}/check`);
      return response.status === "success";
    } catch (error) {
      return isCalCredentialDefinitivelyUnusableError(error) ? false : undefined;
    }
  }

  /**
   * Fetches all connected calendars for the user
   *
   * @returns List of connected calendar credentials
   */
  async getCalendars(): Promise<GetCalendarsResponse["data"]> {
    const response = await this.get<GetCalendarsResponse>("/calendars");
    return response.data;
  }

  /**
   * Drops one external calendar connection for the current managed user.
   *
   * `credentialId` names the connection, not the calendar: Cal reports it on every
   * `connectedCalendars` entry, and one connection can hold many calendars. Disconnecting takes
   * all of them, which is what the clinician asked for when they disconnect the account.
   */
  async disconnectCalendar({
    integrationType,
    credentialId,
  }: DisconnectCalendarRequest): Promise<void> {
    const slug = CALENDAR_INTEGRATION_SLUGS[integrationType];
    if (!slug) {
      throw new Error(`Cal has no disconnect route for integration "${integrationType}"`);
    }

    const response = await this.post<{ status: "success" | "error" }>(
      `/calendars/${slug}/disconnect`,
      { id: credentialId },
    );

    // Cal answers 200 with `status: "error"` on a connection it could not drop, so the HTTP
    // status alone would report a failed disconnect as done.
    if (response.status !== "success") {
      throw new Error(`Failed to disconnect ${integrationType}: Cal returned "${response.status}"`);
    }
  }

  /**
   * Creates a schedule for the current managed user.
   */
  async createSchedule(request: CreateScheduleRequest): Promise<CalSchedule> {
    const response = await this.post<CreateScheduleResponse>("/schedules", request);
    if (response.status !== "success") {
      throw new Error(`Failed to create schedule: API returned status "${response.status}"`);
    }
    return response.data;
  }

  /**
   * Fetches a schedule by ID.
   */
  async getSchedule(scheduleId: number): Promise<CalSchedule> {
    const response = await this.get<GetScheduleResponse>(`/schedules/${scheduleId}`);
    if (response.status !== "success") {
      throw new Error(
        `Failed to get schedule ${scheduleId}: API returned status "${response.status}"`,
      );
    }
    return response.data;
  }

  /**
   * Updates an existing schedule for the current managed user.
   */
  async updateSchedule(scheduleId: number, request: UpdateScheduleRequest): Promise<CalSchedule> {
    const response = await this.patch<UpdateScheduleResponse>(`/schedules/${scheduleId}`, request);
    if (response.status !== "success") {
      throw new Error(
        `Failed to update schedule ${scheduleId}: API returned status "${response.status}"`,
      );
    }
    return response.data;
  }

  /**
   * Fetches busy times from connected calendars within a date range
   *
   * @param request - Parameters including date range, timezone, and calendars to load
   * @returns List of busy time slots normalized to use start/end fields.
   *          Invalid entries (missing start/end) are filtered out.
   */
  async getBusyTimes(request: GetBusyTimesRequest): Promise<CalBusyTime[]> {
    const params = new URLSearchParams({
      loggedInUsersTz: request.loggedInUsersTz,
      dateFrom: request.dateFrom,
      dateTo: request.dateTo,
    });

    request.calendarsToLoad.forEach((cal, index) => {
      params.append(`calendarsToLoad[${index}][credentialId]`, String(cal.credentialId));
      params.append(`calendarsToLoad[${index}][externalId]`, cal.externalId);
    });

    const url = `/calendars/busy-times?${params.toString()}`;
    const response = await this.get<GetBusyTimesResponse>(url);

    // Normalize response to handle both start/end and startTime/endTime formats
    // Filter out invalid entries that are missing start or end times
    return response.data
      .map((item) => {
        const trimmedSource = typeof item.source === "string" ? item.source.trim() : undefined;

        return {
          start: item.start ?? item.startTime ?? "",
          end: item.end ?? item.endTime ?? "",
          source: trimmedSource && trimmedSource.length > 0 ? trimmedSource : undefined,
        };
      })
      .filter((item) => item.start.length > 0 && item.end.length > 0);
  }

  /**
   * Fetches bookable slots for a single event type on Cal.com.
   * Use eventTypeId for managed child events when available; otherwise slug + username.
   */
  async getAvailableSlots(request: GetAvailableSlotsRequest): Promise<CalAvailableSlot[]> {
    const params = new URLSearchParams({
      start: request.start,
      end: request.end,
    });

    if (request.eventTypeId !== undefined) {
      params.set("eventTypeId", String(request.eventTypeId));
    } else {
      if (!request.eventTypeSlug?.trim() || !request.username?.trim()) {
        throw new Error(
          "Provide eventTypeId or eventTypeSlug + username when fetching Cal.com slots",
        );
      }

      params.set("eventTypeSlug", request.eventTypeSlug.trim());
      params.set("username", request.username.trim());
    }

    if (request.timeZone) {
      params.set("timeZone", request.timeZone);
    }

    if (request.duration !== undefined) {
      params.set("duration", String(request.duration));
    }

    if (request.format) {
      params.set("format", request.format);
    }

    if (request.bookingUidToReschedule) {
      params.set("bookingUidToReschedule", request.bookingUidToReschedule);
    }

    const response = await this.get<GetAvailableSlotsResponse>(`/slots?${params.toString()}`, {
      apiVersion: CAL_API_VERSION_SLOTS,
    });

    return Object.values(response.data ?? {})
      .flat()
      .filter((slot) => slot.start.length > 0);
  }

  async get<T>(url: string, options: { apiVersion?: string } = {}): Promise<T> {
    return this.fetchWithInvalidTokenRetry(({ accessToken, shouldCaptureInvalidAccessToken }) =>
      fetchFromCalWithRetry<T>(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        alertContext: this.options.alertContext,
        calApiVersion: options.apiVersion,
        shouldCaptureInvalidAccessToken,
      }),
    );
  }

  async post<T>(url: string, body?: unknown, options: { apiVersion?: string } = {}): Promise<T> {
    return this.fetchWithInvalidTokenRetry(({ accessToken, shouldCaptureInvalidAccessToken }) =>
      fetchFromCalWithRetry<T>(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        alertContext: this.options.alertContext,
        body: body ? JSON.stringify(body) : undefined,
        calApiVersion: options.apiVersion,
        shouldCaptureInvalidAccessToken,
      }),
    );
  }

  async delete<T>(url: string, options: { apiVersion?: string } = {}): Promise<T> {
    return this.fetchWithInvalidTokenRetry(({ accessToken, shouldCaptureInvalidAccessToken }) =>
      fetchFromCalWithRetry<T>(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        alertContext: this.options.alertContext,
        calApiVersion: options.apiVersion,
        shouldCaptureInvalidAccessToken,
      }),
    );
  }

  async patch<T>(url: string, body?: unknown, options: { apiVersion?: string } = {}): Promise<T> {
    return this.fetchWithInvalidTokenRetry(({ accessToken, shouldCaptureInvalidAccessToken }) =>
      fetchFromCalWithRetry<T>(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        alertContext: this.options.alertContext,
        body: body ? JSON.stringify(body) : undefined,
        calApiVersion: options.apiVersion,
        shouldCaptureInvalidAccessToken,
      }),
    );
  }

  private async fetchWithInvalidTokenRetry<T>(
    request: (input: CalClientRequestInput) => Promise<T>,
  ): Promise<T> {
    const shouldCaptureInitialInvalidAccessToken =
      this.options.shouldCaptureInvalidAccessToken ?? !this.options.refreshAccessToken;

    try {
      return await request({
        accessToken: this.accessToken,
        shouldCaptureInvalidAccessToken: shouldCaptureInitialInvalidAccessToken,
      });
    } catch (error) {
      if (!isInvalidCalAccessTokenError(error) || !this.options.refreshAccessToken) {
        throw error;
      }

      let refreshedToken: string;
      try {
        refreshedToken = await this.options.refreshAccessToken({
          currentAccessToken: this.accessToken,
          forceRefresh: true,
        });
      } catch (refreshError) {
        await this.captureTerminalInvalidAccessToken(request);
        throw refreshError;
      }

      if (!refreshedToken) {
        await this.captureTerminalInvalidAccessToken(request);
        throw new Error("Cal access token unavailable after forced refresh");
      }

      this.accessToken = refreshedToken;
      return request({
        accessToken: this.accessToken,
        shouldCaptureInvalidAccessToken: true,
      });
    }
  }

  private async captureTerminalInvalidAccessToken<T>(
    request: (input: CalClientRequestInput) => Promise<T>,
  ): Promise<void> {
    try {
      await request({
        accessToken: this.accessToken,
        shouldCaptureInvalidAccessToken: true,
      });
    } catch {
      // capture side effect only; preserve the refresh failure callers need to handle
    }
  }
}

function getCalRequestAlertContext({
  calCredentials,
  alertContext,
}: {
  calCredentials: CalCredentials;
  alertContext?: CalRequestAlertContext;
}): CalRequestAlertContext {
  return {
    calCredentialsId: calCredentials.id,
    memberId: calCredentials.memberId,
    providerId: calCredentials.providerId,
    roomId: calCredentials.roomId,
    ...alertContext,
  };
}

export async function createCalClient(
  calCredentials: CalCredentials,
  options: CreateCalClientOptions = {},
): Promise<CalClient> {
  const {
    alertContext,
    forceRefresh,
    shouldCaptureInvalidAccessToken,
    shouldRetryInvalidAccessToken = true,
  } = options;
  const token = await refreshCredentialsIfExpired(
    calCredentials,
    forceRefresh ? { forceRefresh } : {},
  );
  if (!token) {
    throw new Error("Cal access token unavailable after refresh");
  }
  return new CalClient(token, {
    alertContext: getCalRequestAlertContext({
      calCredentials,
      alertContext,
    }),
    shouldCaptureInvalidAccessToken,
    ...(shouldRetryInvalidAccessToken
      ? {
          refreshAccessToken: (refreshOptions: RefreshCalClientAccessTokenOptions) =>
            refreshCredentialsIfExpired(calCredentials, refreshOptions),
        }
      : {}),
  });
}

/**
 * Runs one Cal operation, forcing a token refresh and retrying once if Cal rejects
 * the managed-user token before our stored expiry says it should be invalid.
 */
export async function runWithCalClientInvalidTokenRetry<T>({
  alertContext,
  calCredentials,
  onInvalidAccessTokenRetry,
  operation,
}: RunWithCalClientInvalidTokenRetryInput<T>): Promise<T> {
  const calClient = await createCalClient(calCredentials, {
    ...(alertContext ? { alertContext } : {}),
    shouldCaptureInvalidAccessToken: false,
    shouldRetryInvalidAccessToken: false,
  });

  try {
    return await operation(calClient);
  } catch (error) {
    if (!isInvalidCalAccessTokenError(error)) {
      throw error;
    }

    onInvalidAccessTokenRetry?.();
    const refreshedCalClient = await createCalClient(calCredentials, {
      forceRefresh: true,
      ...(alertContext ? { alertContext } : {}),
      shouldRetryInvalidAccessToken: false,
    });
    return operation(refreshedCalClient);
  }
}
